import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {
    MenuCategoryData,
    MenuExtraGroupData,
    MenuPlgSellableData,
    MenuItemData
} from "../../../menu/menu-entity.model";
import {ResponsiveService} from "../../../orchestration/responsive.service";
import {NotificationService} from "../../../notification/notification.service";
import {Subscription} from "rxjs/Subscription";
import {ButtonProminence, ButtonTier} from "../../../buttons/button/button";
import {NavigationService} from "../../../orchestration/navigation.service";
import {OrderItemOptionLayout} from "./order-item-option-control/order-item-option-control.component";
import {PromoModel} from "../../../promo/promo.model";
import {MenuItemSize, MenuModel} from "../../../menu/menu.model";
import {IdentityModel} from "../../../identity/identity.model";
import {OrderModel} from "../../order.model";
import {OrderingContextModel} from "../../../ordering-context/ordering-context.model";
import {OrderEvents} from "../../order-events";
import {SessionEvents} from "../../../orchestration/session-events";
import {OrderCompositeItem, OrderItem, OrderPlgItem} from "../../order";

@Component({
    selector: "woa-order-item-options-panel",
    templateUrl: "./order-item-options-panel.component.html"
})
export class OrderItemOptionsPanelComponent implements OnInit {
    @Input() originalOrderItem: OrderItem;
    @Input() category: MenuCategoryData;
    @Input() showSizes: boolean = true;
    @Input() showOptions: boolean = true;

    @Output() onSizeSelect: EventEmitter<MenuItemData> = new EventEmitter();
    @Output() onItemChange: EventEmitter<void> = new EventEmitter();
    @Output() onExtraChange: EventEmitter<MenuExtraGroupData> = new EventEmitter();

    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    OrderItemOptionLayout = OrderItemOptionLayout;
    AppOrderModel = OrderModel;
    OrderEvents = OrderEvents;
    SessionEvents = SessionEvents;

    menuItemSizes: MenuItemSize[];
    selectedSize: MenuItemSize;
    minimalMode: boolean;

    /**
     * Contains the list of plg sellables that will show to the user.
     * It filters based on the menu item settings for the plg item.
     * @type {Map<any, any>}
     */
    availablePlgSellables: Map<string, MenuPlgSellableData[]> = new Map();

    plgHasImages: Map<string, boolean> = new Map();

    private _orderItem: OrderItem;
    private subscriptions: Subscription = new Subscription();
    private originalSelectedSize: MenuItemSize;

    constructor(public orderingContextModel: OrderingContextModel,
                public orderModel: OrderModel,
                public menuModel: MenuModel,
                public responsiveService: ResponsiveService,
                private promoModel: PromoModel,
                private identityModel: IdentityModel,
                private changeDetectorRef: ChangeDetectorRef,
                private notificationService: NotificationService,
                private navigationService: NavigationService) {
    }

    ngOnInit(): void {
        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => this.changeDetectorRef.markForCheck()));

        this.minimalMode = this.responsiveService.mobile && this.menuModel.itemHasCompositeProductInSizes(this.orderItem.menuItem);

        if (this.orderItem.menuItem.sellableSizeGroup) {
            this.menuItemSizes = this.menuModel.getItemSizes(this.orderItem.menuItem)
                .filter((size: MenuItemSize) => this.menuModel.itemIsAvailableOnSchedules(size.menuItem))
                .map((size: MenuItemSize) => {
                    this.menuModel.initialiseItem(size.menuItem);
                    this.promoModel.getPromotedFeatureTag(this.category, size.menuItem);
                    return size;
                });
            if (this.menuItemSizes) {
                this.selectedSize = this.menuItemSizes.find((touchSize: MenuItemSize) => touchSize.menuItem.sellable.sellableId === this.orderItem.sellable.sellableId);
                this.originalSelectedSize = this.selectedSize;
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get isCompositeItem(): boolean {
        return this._orderItem instanceof OrderCompositeItem;
    }

    get orderCompositeItem(): OrderCompositeItem {
        return this.isCompositeItem ? <OrderCompositeItem>this._orderItem : undefined;
    }

    get orderItem(): OrderItem {
        return this._orderItem;
    }

    get showSinglePlgItem(): boolean {
        return this.responsiveService.mobile;
    }

    @Input()
    set orderItem(orderItem: OrderItem) {
        this._orderItem = orderItem;
        if (this.isCompositeItem) {
            this.orderCompositeItem.items.forEach((componentItem: OrderItem) => {
                this.availablePlgSellables.set(componentItem.identifier, []);
                if (componentItem instanceof OrderPlgItem) {
                    const plgItems: MenuPlgSellableData[] = [];
                    componentItem.plg.sellableLines.forEach((plgItem: MenuPlgSellableData) => {
                        if (!this.menuModel.plgItemIsAvailable(plgItem.menuItem, this.orderCompositeItem.menuItem, this._orderItem ? this._orderItem.selectedFilterTags : undefined)) {
                            return;
                        }
                        if (plgItem.menuItem && !!this.menuModel.getStandardItemImage(plgItem.menuItem)) {
                            this.plgHasImages.set(componentItem.identifier, true);
                        }
                        plgItems.push(plgItem);
                    });
                    this.availablePlgSellables.set(componentItem.identifier, plgItems);
                }
            });
        }
    }

    get sizesTitle(): string {
        return this.menuItemSizes[0].sellableSize.externalSizeName ? "woa.heading.order.sizes.select" : "woa.heading.order.sizes.options";
    }

    public sizeItemAtLimit(size: MenuItemSize): boolean {
        if (size === this.selectedSize || size === this.originalSelectedSize) {
            return false;
        }
        return this.orderModel.menuItemAtLimit(size.menuItem, 1, 0, this.originalOrderItem);
    }

    getSizePrice(size: MenuItemSize): number {
        if (OrderModel.SHOW_RELATIVE_PRICING_FOR_SIZES) {
            const price: number = size.menuItem.sellable.defaultPrice - this.originalSelectedSize.menuItem.sellable.defaultPrice;
            return price !== 0 ? price : undefined;
        }
        return size.menuItem.sellable.defaultPrice;
    }

    getSizeGuestPrice(size: MenuItemSize): number {
        if (OrderModel.SHOW_RELATIVE_PRICING_FOR_SIZES) {
            const price: number = size.menuItem.sellable.defaultGuestPrice - this.originalSelectedSize.menuItem.sellable.defaultGuestPrice;
            return price !== 0 ? price : undefined;
        }
        return size.menuItem.sellable.defaultGuestPrice;
    }

    public async selectSize(size: MenuItemSize): Promise<void> {
        if (this.sizeItemAtLimit(size)) {
            if (this.menuModel.itemOutOfStock(size.menuItem)) {
                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: size.menuItem.name});
            } else {
                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: size.menuItem.name});
            }
            event.preventDefault();
            return;
        }

        if (this.orderingContextModel.currentStore.minimumAge && size.menuItem.sellable.applyMinimumAge && !await this.identityModel.verifyAge()) {
            return;
        }

        const qtyAvailable: number = this.menuModel.sellableQtyAvailable(size.menuItem.sellable.sellableId);
        if (this.orderItem.quantity > qtyAvailable) {
            this.orderItem.quantity = qtyAvailable;
        }
        if (this.orderItem.quantity < size.menuItem.sellable.minWebOrderQty) {
            this.orderItem.quantity = size.menuItem.sellable.minWebOrderQty;
        }

        this.selectedSize = size;
        this.onSizeSelect.emit(this.selectedSize.menuItem);
    }

    extraChanged(extraGroup: MenuExtraGroupData): void {
        this.onExtraChange.emit(extraGroup);
        this.itemChanged();
    }

    itemChanged(): void {
        this.onItemChange.emit();
        this.changeDetectorRef.detectChanges();
    }

    isOrderPlgItem(item: OrderItem): boolean {
        return item instanceof OrderPlgItem;
    }

    asOrderPlgItem(item: OrderItem): OrderPlgItem {
        return <OrderPlgItem>item;
    }

    showCompositeItem(item: OrderItem): boolean {
        return !this.isOrderPlgItem(item) || !!(<OrderPlgItem>item).plg.sellableLines.length;
    }

    plgName(item: OrderItem): string {
        return item instanceof OrderPlgItem ? ((<OrderPlgItem>item).plg.externalName || (<OrderPlgItem>item).plg.name) : undefined;
    }

    compositeItemName(item: OrderItem): string {
        return item.sellable.menuItem && item.sellable.menuItem.name ? item.sellable.menuItem.name : item.name;
    }

    plgSellableAtLimit(plgSellable: MenuPlgSellableData): boolean {
        return this.orderModel.sellableAtLimit(plgSellable.menuItem ? plgSellable.menuItem.sellable.sellableId : plgSellable.sellable.sellableId, this._orderItem.quantity, this._orderItem.sellableQtyOrdered(this._orderItem.quantity, plgSellable.sellable.sellableId));
    }

    isPlgSellableSelected(plgItem: OrderPlgItem, plgSellable: MenuPlgSellableData): boolean {
        return (plgItem.sellable && plgSellable.sellable && plgSellable.sellable.sellableId === plgItem.sellable.sellableId);
    }

    async selectPlgSellable(plgItem: OrderPlgItem, plgSellable?: MenuPlgSellableData, event?: Event): Promise<void> {
        if (!(plgItem instanceof OrderPlgItem) || (plgSellable && this.isPlgSellableSelected(plgItem, plgSellable))) {
            event && event.stopPropagation();
            return;
        }

        if (plgSellable) {
            if (this.plgSellableAtLimit(plgSellable)) {
                if (this.menuModel.itemOutOfStock(plgSellable.menuItem)) {
                    // noinspection ES6MissingAwait
                    this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: plgSellable.menuItem.name});
                } else {
                    // noinspection ES6MissingAwait
                    this.notificationService.displayAlert(null, "woa.message.order.productLimitReached", {item: plgSellable.menuItem.name});
                }
                event && event.stopPropagation();
                return;
            }

            if (this.orderingContextModel.currentStore.minimumAge && plgSellable.sellable.applyMinimumAge && !await this.identityModel.verifyAge()) {
                event && event.stopPropagation();
                return;
            }

            plgItem.selectSellable(plgSellable.sellable, this.orderCompositeItem);
        } else if (this.responsiveService.mobile) {
            const newItem: OrderPlgItem = await this.navigationService.createPage("woa-order-item-plg-edit-page", {
                orderItem: this.orderItem,
                currentItem: <OrderPlgItem>plgItem,
                menuItem: this.orderItem.menuItem
            });
            if (!newItem || ((<OrderPlgItem>plgItem).sellable && (<OrderPlgItem>plgItem).sellable.sellableId === newItem.sellable.sellableId)) {
                return;
            }

            this.orderCompositeItem.replaceOrderItem((<OrderPlgItem>plgItem).identifier, newItem);
        }

        this.itemChanged();
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order-item/order-item-options-panel/order-item-options-panel.component.ts